import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Spinner,
  Row,
  Col,
} from 'reactstrap'
import QRCode from 'react-qr-code'
import { Printer, ArrowLeft } from 'react-feather'
import BreadCrumbs from 'components/breadcrumbs'
import { useFetchTransactionByIdQuery } from '../dashboard/dashboardApiSlice'
import { thermalPrinter } from 'utils/thermalPrinter'
import { URL_DETAIL_TRANSACTION } from 'utils/pathUrl'
import moment from 'moment-timezone'
import pmlogo from 'assets/images/pmlogo.png'
import { renderQrWithLogoToCanvas } from 'utils/qrLogoHelper'
import FormatProcessor from 'utils/FormatProcessor'

const isGroupNode = (node) =>
  Boolean(node?.groupLabel && Array.isArray(node?.items))

const flattenItems = (items) => {
  if (!Array.isArray(items)) return []
  const result = []
  for (const node of items) {
    if (isGroupNode(node)) {
      result.push(...flattenItems(node.items))
    } else {
      result.push(node)
    }
  }
  return result
}

const TransactionQRPrint = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state
  const qrRefs = useRef({})
  const logoRef = React.useRef(null)

  const { data, isLoading } = useFetchTransactionByIdQuery({
    id,
    escrowpaymentId: state?.escrowpaymentId,
    tradeshortcutId: state?.tradeshortcutId,
  })

  const transaction = useMemo(() => {
    if (state?.transaction) return state.transaction
    if (Array.isArray(data)) return data[0]
    if (data?.items && Array.isArray(data.items)) return data.items[0]
    if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
      return data.data
    }
    return data || null
  }, [data, state?.transaction])

  const [isConnected, setIsConnected] = useState(thermalPrinter.isConnected)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)

  useEffect(() => {
    const tryAutoConnect = async () => {
      setIsConnecting(true)
      const success = await thermalPrinter.autoConnect()
      setIsConnecting(false)
      if (success) {
        setIsConnected(true)
      }
    }
    tryAutoConnect()
  }, [])

  const handleToggleConnect = async () => {
    if (isConnected) {
      await thermalPrinter.disconnect()
      setIsConnected(false)
    } else {
      setIsConnecting(true)
      try {
        const success = await thermalPrinter.connect()
        if (success) setIsConnected(true)
      } catch (err) {
        alert('Gagal menghubungkan printer.')
      } finally {
        setIsConnecting(false)
      }
    }
  }

  const qrItems = useMemo(() => {
    if (!transaction) return []
    const rawItems = transaction.itemDetail || transaction.orderItems || []
    const items = flattenItems(rawItems)

    return items.map((item, index) => {
      const rid =
        transaction.orderDetail?.poNumber ||
        transaction.orderDetail?.transaction_number ||
        (transaction.id ? `TRX-${transaction.id}` : '')
      const src = transaction.sellerDetail?.fullName || ''
      const isu = transaction.sellerDetail?.fullName || '' // Owner of inventory
      const date = moment(transaction.trxDate || transaction.createdAt).format(
        'YYYY-MM-DDTHH:mm',
      )
      const warehouse =
        transaction.warehouseCode || transaction.location || 'N/A'

      const rawExpiry = item.expiryDate || item.expiry || ''
      const formattedExpiry =
        rawExpiry && moment(rawExpiry).isValid()
          ? moment(rawExpiry).format('YYMMDD')
          : ''

      const itemName =
        item.nameItem ||
        item.commodityName ||
        item.groupLabel ||
        item.name ||
        'COMMODITY'

      const serial =
        item.inventoryOwnerCode || item.inventoryId || item.id || ''
      const batch = item.lotCode || item.lotId || item.batch || ''

      const qrValue = FormatProcessor.generate('PM-Inventory', {
        gtin: item.gtin || item.sku || '',
        serial: serial,
        batch: batch,
        isu: isu,
        productDescription: itemName,
        expiry: formattedExpiry,
        rid: rid,
        src: src,
        d: date,
        l: warehouse,
      })

      return {
        id: item.id || item.inventoryOwnerCode || item.inventoryId || index,
        name: itemName,
        value: qrValue,
      }
    })
  }, [transaction])


  const handlePrint = async () => {
    if (!thermalPrinter.isConnected) {
      alert('Printer belum terhubung.')
      return
    }

    if (qrItems.length === 0) {
      alert('Tidak ada item untuk dicetak.')
      return
    }

    setIsPrinting(true)
    try {
      for (const item of qrItems) {
        const title = transaction?.orderDetail?.poNumber || 'TRANSACTION'
        const subtitle = item.name
        const qrContainer = qrRefs.current[item.id]
        
        if (!qrContainer) continue

        const svgElement = qrContainer.querySelector('svg')
        const logoElement = logoRef.current

        await thermalPrinter.init()
        await thermalPrinter.center()
        
        // Print Labels as Text
        await thermalPrinter.write(new Uint8Array([0x1b, 0x45, 0x01])) // Bold ON
        await thermalPrinter.text(title.toUpperCase())
        await thermalPrinter.write(new Uint8Array([0x1b, 0x45, 0x00])) // Bold OFF
        await thermalPrinter.text(subtitle)
        await thermalPrinter.feed(1)

        // Render QR with Logo to Canvas and print as Image
        const canvas = await renderQrWithLogoToCanvas(svgElement, logoElement)
        if (canvas) {
          await thermalPrinter.printImage(canvas)
        } else {
          // Fallback if canvas render fails
          await thermalPrinter.printQR(item.value)
        }
        
        await thermalPrinter.feed(4)
        
        // Small delay between items
        await new Promise(resolve => setTimeout(resolve, 500))
      }
    } catch (err) {
      console.error(err)
      alert('Gagal mencetak.')
    } finally {
      setIsPrinting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" />
      </div>
    )
  }

  const isTransactionValid = Boolean(
    transaction &&
      (transaction.id ||
        transaction.orderDetail ||
        transaction.itemDetail ||
        transaction.orderItems),
  )

  if (!isTransactionValid) {
    return (
      <div className="text-center mt-5">
        <p>Transaksi tidak ditemukan</p>
        <Button color="primary" onClick={() => navigate(-1)}>
          Kembali
        </Button>
      </div>
    )
  }

  return (
    <div className="w-100 px-3 pb-5">
      <div className="mt-3 no-print">
        <BreadCrumbs
          breadCrumbActive="Print QR Transaksi"
          breadCrumbParent="Detail Transaksi"
          breadCrumbParentLink={`${URL_DETAIL_TRANSACTION}/${id}${location.search || ''}`}
        />
      </div>

      <Row className="justify-content-center mt-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-transparent border-bottom-0 pt-3 d-flex justify-content-between">
              <h5 className="fw-semibold mb-0">Print QR Transaksi</h5>
              <Button color="flat-secondary" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft size={14} className="me-1" />
                Kembali
              </Button>
            </CardHeader>
            <CardBody className="text-center py-4">
              <div className="mb-4 text-start">
                <h6 className="fw-bold mb-3">Item yang akan dicetak ({qrItems.length}):</h6>
                {qrItems.length === 0 ? (
                  <p className="text-muted text-center my-3">
                    Tidak ada item untuk dicetak pada transaksi ini
                  </p>
                ) : (
                  qrItems.map((item, idx) => (
                    <div
                      key={item.id}
                      className={`p-3 mb-3 bg-white shadow-sm rounded border-start border-4 ${idx % 2 === 0 ? 'border-primary' : 'border-info'}`}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          ref={(el) => (qrRefs.current[item.id] = el)}
                          className="qr-container"
                          style={{ position: 'relative' }}
                        >
                          <QRCode value={item.value} size={80} />
                          <img
                            src={pmlogo}
                            alt="logo"
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '20px',
                              height: '20px',
                              backgroundColor: 'white',
                              padding: '2px',
                              borderRadius: '2px',
                            }}
                          />
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <h6 className="fw-bold mb-1">{item.name}</h6>
                          <p className="small text-muted mb-0 text-truncate">
                            {item.value}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Hidden logo for canvas rendering */}
              <img ref={logoRef} src={pmlogo} alt="logo-hidden" style={{ display: 'none' }} />

              <div className="d-flex flex-column gap-2 px-md-5">
                {isConnected ? (
                  <Button
                    color="primary"
                    onClick={handlePrint}
                    disabled={isPrinting}
                  >
                    {isPrinting ? (
                      <Spinner size="sm" className="me-2" />
                    ) : (
                      <Printer size={16} className="me-2" />
                    )}
                    Print QR
                  </Button>
                ) : (
                  <Button
                    color="warning"
                    onClick={handleToggleConnect}
                    disabled={isConnecting}
                  >
                    {isConnecting ? (
                      <Spinner size="sm" className="me-2" />
                    ) : (
                      <Printer size={16} className="me-2" />
                    )}
                    Hubungkan Printer
                  </Button>
                )}
                
                {isConnected && (
                  <Button
                    color="flat-danger"
                    size="sm"
                    onClick={handleToggleConnect}
                    disabled={isConnecting}
                  >
                    Putuskan Printer
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default TransactionQRPrint
